import { Box, LinearProgress, Typography } from "@mui/material";
import { onboardingTokens } from "../theme";
import { t } from "../../../locales/t";

/**
 * The onboarding progress indicator.
 *
 * Takes a fraction rather than deriving one, because progress legitimately
 * lands between steps: a multi-screen sub-flow advances the bar part-way
 * through its own step (see `useProgress`). The step count beside it is a
 * label, and is whole numbers because "3.4 of 8" helps nobody.
 *
 * Built on MUI's LinearProgress rather than a bare div so the accessibility
 * work comes for free: it renders `role="progressbar"` with `aria-valuenow`,
 * which a screen reader announces as the member advances.
 *
 * @param {object} props
 * @param {number} props.fraction    0 to 1
 * @param {number} props.stepNumber  1-based position in the flow
 * @param {number} props.stepTotal   fixed for the pathway, known from the start
 */
const ProgressBar = ({ fraction, stepNumber, stepTotal }) => {
  const percent = Math.round(fraction * 100);

  return (
    <Box sx={{ width: "100%" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          mb: 1,
        }}
      >
        <Typography
          variant="caption"
          sx={{
            fontFamily: onboardingTokens.fontBody,
            fontSize: "0.75rem",
            color: onboardingTokens.mutedForeground,
          }}
        >
          {t("onboarding.progress.count", {
            current: stepNumber,
            total: stepTotal,
          })}
        </Typography>
      </Box>

      <LinearProgress
        variant="determinate"
        value={percent}
        aria-label={t("onboarding.progress.label")}
        sx={{
          height: 4,
          borderRadius: 999,
          backgroundColor: onboardingTokens.track,
          "& .MuiLinearProgress-bar": {
            borderRadius: 999,
            backgroundColor: onboardingTokens.teal,
            // MUI's default is a 4s linear transform, tuned for indeterminate
            // bars and far too slow here — the bar would still be sliding a
            // step later. The design's 500ms ease-out lands before the next
            // screen settles.
            transition: "transform 0.5s cubic-bezier(0.16, 1, 0.3, 1)",
          },
          // Respect a reduced-motion preference rather than animating anyway.
          "@media (prefers-reduced-motion: reduce)": {
            "& .MuiLinearProgress-bar": { transition: "none" },
          },
        }}
      />
    </Box>
  );
};

export default ProgressBar;
